import React, { useEffect, useMemo, useState } from 'react';
import { DollarSign, Package, AlertTriangle, Clock } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { getInventoryDashboardMetrics } from '../../utils/inventoryApi';
import type { InventoryDashboardMetrics } from '../../types/inventory';

export const InventoryDashboard: React.FC = () => {
  const { user } = useAuth();
  const practiceId = user?.practice_id;

  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<InventoryDashboardMetrics | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const pid = practiceId;
    if (!pid) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getInventoryDashboardMetrics(pid);
        if (cancelled) return;
        setMetrics(data);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message || 'Failed to load dashboard');
        setMetrics(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [practiceId]);

  const cards = useMemo(() => {
    const m = metrics;
    return [
      {
        label: 'Estimated revenue on hand',
        value: m ? `$${Number(m.estimated_revenue_on_hand || 0).toFixed(2)}` : '—',
        icon: <DollarSign className="w-5 h-5 text-green-600" />,
        iconBg: 'bg-green-100',
      },
      {
        label: 'Total on-hand units',
        value: m ? String(m.total_on_hand_units ?? 0) : '—',
        icon: <Package className="w-5 h-5 text-blue-600" />,
        iconBg: 'bg-blue-100',
      },
      {
        label: 'Low stock',
        value: m ? String(m.low_stock_count ?? 0) : '—',
        icon: <AlertTriangle className="w-5 h-5 text-amber-600" />,
        iconBg: 'bg-amber-100',
      },
      {
        label: 'Expiring soon (60d)',
        value: m ? String(m.expiring_soon_count ?? 0) : '—',
        icon: <Clock className="w-5 h-5 text-indigo-600" />,
        iconBg: 'bg-indigo-100',
      },
    ];
  }, [metrics]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <div className="text-sm text-gray-600">Loading dashboard…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <div className="text-sm text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Metric cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs font-medium text-gray-500">{c.label}</div>
                <div className="text-xl font-semibold text-gray-900 mt-1">{c.value}</div>
              </div>
              <div className={`p-2 rounded-lg ${c.iconBg}`}>{c.icon}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Top consumed + Recent POs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="text-sm font-semibold text-gray-900">Top consumed (last 30 days)</div>
            <div className="text-xs text-gray-500 mt-1">Shows known COGS; unknown cost is tracked separately.</div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Item</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Type</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Qty</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase">COGS</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(metrics?.top_consumed_items || []).length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-8 text-sm text-gray-500 text-center">
                      No consumption yet.
                    </td>
                  </tr>
                ) : (
                  metrics!.top_consumed_items.map((r) => (
                    <tr key={`${r.catalog_type}:${r.catalog_item_id}`} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <div className="font-medium">{r.name}</div>
                        {r.unknown_cost_quantity > 0 ? (
                          <div className="text-xs text-gray-500 mt-0.5">
                            Unknown cost: {r.unknown_cost_quantity}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">{r.catalog_type}</td>
                      <td className="px-6 py-4 text-sm text-gray-900 text-right">{r.total_consumed_quantity}</td>
                      <td className="px-6 py-4 text-sm text-gray-900 text-right">
                        ${Number(r.total_cogs || 0).toFixed(2)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="text-sm font-semibold text-gray-900">Recent purchase orders</div>
            <div className="text-xs text-gray-500 mt-1">Last 10 by order date.</div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">PO</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Vendor</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(metrics?.recent_purchase_orders || []).length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-8 text-sm text-gray-500 text-center">
                      No purchase orders yet.
                    </td>
                  </tr>
                ) : (
                  metrics!.recent_purchase_orders.map((po) => (
                    <tr key={po.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <div className="font-medium">{po.po_number}</div>
                        <div className="text-xs text-gray-500 mt-0.5">{po.status}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">{po.vendor_name || '—'}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{po.order_date}</td>
                      <td className="px-6 py-4 text-sm text-gray-900 text-right">
                        ${Number(po.total_cost || 0).toFixed(2)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};


